/**
 * Форматує значення знижки залежно від типу
 * @param {Object} discount - об'єкт знижки
 * @returns {string} - відформатоване значення знижки
 */
export const formatDiscountValue = (discount) => {
  if (!discount) return '';
  
  switch (discount.type) {
    case 'PERCENTAGE':
      return discount.percentageValue;
    case 'FIXED':
      return `${discount.fixedValue} грн`;
    case 'FREE_SHIPPING':
      return 'Безкоштовна доставка';
    case 'BUY_ONE_GET_ONE':
      return '2 за ціною 1';
    case 'PROMOTIONAL':
      return discount.description || 'Акційна пропозиція';
    case 'SPECIAL_OFFER':
      if (discount.priceRangeMin && discount.priceRangeMax) {
        return `${discount.priceRangeMin}–${discount.priceRangeMax} грн`;
      }
      return discount.description || 'Спеціальна пропозиція';
    default:
      return discount.description || 'Знижка';
  }
};

/**
 * Форматує дату у зручному для читання форматі
 * @param {string|Date} date - дата для форматування
 * @param {Object} options - опції форматування
 * @returns {string} - відформатована дата
 */
export const formatDate = (date, options = {}) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };
  
  return dateObj.toLocaleDateString('uk-UA', defaultOptions);
};

/**
 * Форматує дату з часом
 * @param {string|Date} date - дата для форматування
 * @returns {string} - відформатована дата з часом
 */
export const formatDateTime = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  return dateObj.toLocaleString('uk-UA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Форматує відносну дату (наприклад, "2 дні тому")
 * @param {string|Date} date - дата для форматування
 * @returns {string} - відносна дата
 */
export const formatRelativeDate = (date) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  const now = new Date();
  const diffInMs = now - dateObj;
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  
  if (diffInDays > 7) {
    return formatDate(date);
  } else if (diffInDays > 0) {
    return `${diffInDays} ${diffInDays === 1 ? 'день' : diffInDays < 5 ? 'дні' : 'днів'} тому`;
  } else if (diffInHours > 0) {
    return `${diffInHours} ${diffInHours === 1 ? 'година' : diffInHours < 5 ? 'години' : 'годин'} тому`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'хвилина' : diffInMinutes < 5 ? 'хвилини' : 'хвилин'} тому`;
  } else {
    return 'Щойно';
  }
};

/**
 * Перевіряє, чи знижка закінчується незабаром
 * @param {string|Date} endDate - дата закінчення знижки
 * @param {number} daysThreshold - кількість днів для попередження (за замовчуванням 7)
 * @returns {boolean} - true, якщо знижка закінчується незабаром
 */
export const isDiscountExpiringSoon = (endDate, daysThreshold = 7) => {
  if (!endDate) return false;
  
  const endDateObj = new Date(endDate);
  const now = new Date();
  const diffInMs = endDateObj - now;
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  
  return diffInDays <= daysThreshold && diffInDays > 0;
};

/**
 * Перевіряє, чи знижка активна на поточний момент
 * @param {Object} discount - об'єкт знижки
 * @returns {boolean} - true, якщо знижка активна
 */
export const isDiscountActive = (discount) => {
  if (!discount) return false;
  
  const now = new Date();
  
  // Перевіряємо статус активності
  if (!discount.active) return false;
  
  // Перевіряємо дату початку
  if (discount.startDate && new Date(discount.startDate) > now) {
    return false;
  }
  
  // Перевіряємо дату закінчення
  if (discount.endDate && new Date(discount.endDate) < now) {
    return false;
  }
  
  return true;
};

/**
 * Перевіряємо, чи знижка закінчилась
 * @param {string|Date} endDate - дата закінчення знижки
 * @returns {boolean} - true, якщо знижка закінчилась
 */
export const isDiscountExpired = (endDate) => {
  if (!endDate) return false;
  
  const endDateObj = new Date(endDate);
  const now = new Date();
  
  return endDateObj < now;
};

/**
 * Розраховує кількість днів до закінчення знижки
 * @param {string|Date} endDate - дата закінчення знижки
 * @returns {number} - кількість днів (від'ємне число, якщо знижка вже закінчилась)
 */
export const getDaysUntilExpiration = (endDate) => {
  if (!endDate) return Infinity;
  
  const endDateObj = new Date(endDate);
  const now = new Date();
  const diffInMs = endDateObj - now;
  
  return Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
};

/**
 * Форматує суму грошей
 * @param {number} amount - сума
 * @param {string} currency - валюта (за замовчуванням 'грн')
 * @returns {string} - відформатована сума
 */
export const formatCurrency = (amount, currency = 'грн') => {
  if (amount === null || amount === undefined) return '0 ' + currency;
  
  const numAmount = Number(amount);
  if (isNaN(numAmount)) return '0 ' + currency;
  
  return `${numAmount.toFixed(2)} ${currency}`;
};

/**
 * Форматує відсоток
 * @param {number} percentage - відсоток
 * @returns {string} - відформатований відсоток
 */
export const formatPercentage = (percentage) => {
  if (percentage === null || percentage === undefined) return '0%';
  
  const numPercentage = Number(percentage);
  if (isNaN(numPercentage)) return '0%';
  
  return `${numPercentage}%`;
};

/**
 * Скорочує текст до зазначеної довжини
 * @param {string} text - текст для скорочення
 * @param {number} maxLength - максимальна довжина
 * @returns {string} - скорочений текст
 */
export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength) + '...';
};

/**
 * Форматує назву статусу знижки
 * @param {Object} discount - об'єкт знижки
 * @returns {Object} - об'єкт з назвою статусу та кольором
 */
export const getDiscountStatusInfo = (discount) => {
  if (!discount) {
    return { label: 'Невідомо', color: 'gray' };
  }
  
  if (!discount.active) {
    return { label: 'Деактивована', color: 'red' };
  }
  
  if (isDiscountExpired(discount.endDate)) {
    return { label: 'Закінчилась', color: 'red' };
  }
  
  if (isDiscountExpiringSoon(discount.endDate)) {
    return { label: 'Скоро закінчується', color: 'amber' };
  }
  
  if (discount.startDate && new Date(discount.startDate) > new Date()) {
    return { label: 'Заплановано', color: 'blue' };
  }
  
  return { label: 'Активна', color: 'green' };
};